import { useMutation, useQuery } from "@apollo/client/react";
import {
  DSButton,
  DSCardWrapper,
  DSDropdown,
  DSToggle,
} from "@components/design-system/molecules";
import { DSModal } from "@components/design-system/organisms";
import {
  faPhone,
  faTrash,
  faSpinner,
  faPlus,
  faSync,
  faUsers,
  faCog,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { UPDATE_VERIFIED_PHONE_SHARING } from "@graphql/mutations/PhoneVerification";
import { CHANGE_SCRIBEPHONE_CONSENT_MODE } from "@graphql/mutations/ScribephoneConsent";
import { GET_SCRIBEPHONE_CONSENT } from "@graphql/queries/ScribephoneConsent";
import { useTeam } from "@hooks";
import { useVerifiedPhoneNumbers } from "@hooks/use-verified-phone-numbers";
import { Tooltip } from "@mui/material";
import React, { useState } from "react";

import {
  getVerificationStatusColor,
  getVerificationStatusText,
} from "./utils";
import { formatPhoneNumberForDisplay } from "../../../../utils/phoneUtils";
import { alert } from "../../../common/Alert";
import { SCRIBEPHONE_CONSENT_MODES } from "../utils";

const getSelectableItemClasses = (isSelected, isClickable = true) => {
  const baseClasses =
    "flex items-center justify-between p-3 rounded-lg border";
  const cursorClasses = isClickable
    ? "cursor-pointer"
    : "cursor-not-allowed";

  const selectionClasses = isSelected
    ? "border-brand bg-brand-subtle hover:bg-indigo-200 dark:hover:bg-indigo-700/50"
    : "border-transparent hover:bg-brand-subtle";

  return `${baseClasses} ${cursorClasses} ${selectionClasses}`;
};

const OverviewStep = ({
  selectedCallerId,
  onSelectCallerId,
  onDeletePhone,
  onAddNewNumber,
  onRefresh,
  deleting,
  defaultPhoneNumber,
  alreadyVerifiedMessage,
  deleteSuccessMessage,
}) => {
  const [updateSharing] = useMutation(UPDATE_VERIFIED_PHONE_SHARING);
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [selectedTeamForSharing, setSelectedTeamForSharing] =
    useState(null);
  const { verifiedPhoneNumbers, loading, refetch } =
    useVerifiedPhoneNumbers();

  const { userTeams } = useTeam();

  const teamOptions = [
    { label: "Don't share", value: "none" },
    ...(userTeams?.map((team) => ({
      label: team.name,
      value: team.uuid,
    })) || []),
  ];

  const { data, loading: loadingConsent } = useQuery(
    GET_SCRIBEPHONE_CONSENT,
  );

  const [changeScribephoneConsentMode] = useMutation(
    CHANGE_SCRIBEPHONE_CONSENT_MODE,
    {
      refetchQueries: [{ query: GET_SCRIBEPHONE_CONSENT }],
    },
  );

  const handleUpdateSharing = async (phoneUuid) => {
    try {
      await updateSharing({
        variables: {
          verifiedPhoneUuid: phoneUuid,
          teamUuid: selectedTeamForSharing,
        },
      });
      setShowSharingModal(false);
      setSelectedTeamForSharing(null);
      // Refetch current user to get updated phone data
      refetch();
    } catch (error) {
      alert("error", "An error occurred. Please try again later.");
      console.error("Error updating sharing:", error);
    }
  };

  const openSharingModal = (phone) => {
    setSelectedTeamForSharing(phone.teamUuid);
    setShowSharingModal(phone);
  };

  const handleSettingsClick = (phone) => {
    if (phone?.twilioVerificationStatus === "DENIED") {
      alert(
        "warning",
        "This phone number is not verified and cannot be used as a caller ID. Please verify a different number.",
      );
      return;
    }

    onSelectCallerId(phone?.phoneNumber);
  };

  const renderPhoneNumber = (phone) => {
    const { teamUuid, isOwnedByCurrentUser } = phone;
    const isSharedWithTeam = teamUuid !== null;
    // We intentionally treat pending as verified and we move it to 'Denied'
    // if there is a failed call attempt with this number
    const isUsable =
      phone.twilioVerificationStatus === "VERIFIED" ||
      phone.twilioVerificationStatus === "PENDING";

    return (
      <div
        key={phone.verifiedPhoneNumberUuid}
        className={getSelectableItemClasses(
          selectedCallerId === phone.phoneNumber,
          isUsable,
        )}
        onClick={() => handleSettingsClick(phone)}
      >
        <div className="flex items-center space-x-3">
          <FontAwesomeIcon
            icon={faPhone}
            className={
              isSharedWithTeam ? "text-brand" : "text-green-500"
            }
          />
          <div>
            <p className="font-medium text-primary">
              {formatPhoneNumberForDisplay(phone?.phoneNumber)}
            </p>
            <p className="text-xs text-secondary">
              <span
                className={`px-2 py-1 rounded-full text-xs ${getVerificationStatusColor(
                  phone.twilioVerificationStatus,
                )}`}
              >
                {getVerificationStatusText(
                  phone.twilioVerificationStatus,
                )}
              </span>
              {phone.twilioVerificationStatus === "VERIFIED" && (
                <span>
                  {" "}
                  • {new Date(phone.createdAt).toLocaleDateString()}
                </span>
              )}
            </p>
            {isSharedWithTeam && (
              <p className="text-xs text-indigo-500 dark:text-indigo-400 mt-2 flex items-center">
                <FontAwesomeIcon icon={faUsers} className="mr-1" />
                {isOwnedByCurrentUser
                  ? `Shared with ${phone.team?.name || "Team"}`
                  : `Shared by ${phone.user?.firstName} ${phone.user?.lastName}`.trim()}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {isOwnedByCurrentUser &&
            userTeams?.length > 0 &&
            isUsable && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openSharingModal(phone);
                }}
                className="text-secondary hover:text-primary"
                title="Manage sharing"
              >
                <FontAwesomeIcon icon={faCog} />
              </button>
            )}
          {isOwnedByCurrentUser && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeletePhone(
                  phone.verifiedPhoneNumberUuid,
                  phone?.phoneNumber,
                );
              }}
              disabled={deleting}
              className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              title="Delete number"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="">
          <div>
            <h4 className="text-sm font-bold text-primary">
              Automatic Verbal Warning (Optional)
            </h4>
            <div className="flex gap-4">
              <p className="text-xs text-secondary flex-1">
                Have Scribephone automatically inform the client that
                the call is being recorded.
              </p>
              <DSToggle
                disabled={!data?.user?.canChangeScribephoneConsent}
                checked={
                  data?.user?.scribephoneConsentMode ==
                  SCRIBEPHONE_CONSENT_MODES.AUTOMATIC
                }
                onChange={() => {
                  if (!data?.user?.canChangeScribephoneConsent) {
                    return;
                  }
                  changeScribephoneConsentMode({
                    variables: {
                      consentMode:
                        data?.user?.scribephoneConsentMode ==
                        SCRIBEPHONE_CONSENT_MODES.MANUAL
                          ? SCRIBEPHONE_CONSENT_MODES.AUTOMATIC
                          : SCRIBEPHONE_CONSENT_MODES.MANUAL,
                    },
                  });
                }}
                dataTestId="scribephone-consent-toggle"
              />
            </div>
            <div className="flex items-center mt-2">
              {data?.user?.canChangeScribephoneConsent ||
              loadingConsent ? null : (
                <p className="text-xs text-yellow-500 dark:text-yellow-400">
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="mr-1"
                  />
                  Your consent mode is managed by your organization
                </p>
              )}
            </div>
          </div>

          <div>
            <div className="h-px bg-secondary my-4"></div>
          </div>

          <div className="flex items-center justify-between">
            <h4 className="text-sm font-bold text-primary">
              Your Phone Numbers
            </h4>
            <Tooltip
              placement="left"
              title="Refresh phone number statuses"
              arrow
            >
              <button
                onClick={onRefresh}
                disabled={loading}
                className="text-secondary hover:text-primary transition-colors"
              >
                <FontAwesomeIcon
                  icon={faSync}
                  className={`text-sm ${loading && "animate-spin"}`}
                />
              </button>
            </Tooltip>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <FontAwesomeIcon
              icon={faSpinner}
              className="animate-spin text-brand"
            />
          </div>
        ) : verifiedPhoneNumbers.length === 0 ? (
          <p className="text-sm text-secondary text-center py-4">
            No phone numbers added yet
          </p>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
            <div
              className={getSelectableItemClasses(
                selectedCallerId === "default",
              )}
              onClick={() => onSelectCallerId("default")}
            >
              <div className="flex items-center space-x-3">
                <FontAwesomeIcon
                  icon={faPhone}
                  className="text-secondary"
                />
                <div>
                  <p className="font-medium text-primary">
                    {formatPhoneNumberForDisplay(defaultPhoneNumber)}
                  </p>
                  <p className="text-xs text-secondary">
                    Scribenote number
                  </p>
                </div>
              </div>
            </div>

            {verifiedPhoneNumbers.map((phone) =>
              renderPhoneNumber(phone),
            )}
          </div>
        )}
      </div>

      {(alreadyVerifiedMessage || deleteSuccessMessage) && (
        <DSCardWrapper variant="success">
          <div className="flex items-center space-x-2">
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="text-success"
            />
            <p className="text-sm text-success">
              {alreadyVerifiedMessage || deleteSuccessMessage}
            </p>
          </div>
        </DSCardWrapper>
      )}

      <div className="flex flex-row items-center justify-center">
        <DSButton variant="primary" onClick={onAddNewNumber}>
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add Phone Number
        </DSButton>
      </div>

      <DSModal
        isOpen={!!showSharingModal}
        onClose={() => setShowSharingModal(false)}
        title="Manage Team Sharing"
        dataTestId="manage-sharing-modal"
        width="w-full max-w-[400px]"
        actions={[
          {
            label: "Cancel",
            variant: "cancel",
            onClick: () => setShowSharingModal(false),
          },
          {
            label: loading ? "Updating..." : "Update",
            variant: "primary",
            onClick: () =>
              handleUpdateSharing(
                showSharingModal?.verifiedPhoneNumberUuid,
              ),
            disabled: loading,
          },
        ]}
      >
        <p className="text-sm mb-4 font-bold">
          Phone Number:{" "}
          {formatPhoneNumberForDisplay(showSharingModal?.phoneNumber)}
        </p>
        <DSDropdown
          options={teamOptions}
          value={selectedTeamForSharing ?? "none"}
          onChange={(val) =>
            setSelectedTeamForSharing(val === "none" ? null : val)
          }
          leadingIcon={faUsers}
          width="w-full"
          dataTestId="manage-sharing-team-selector"
          renderOption={(option) => {
            if (option.value === "none") {
              return <span>{option.label}</span>;
            }
            const team = userTeams?.find(
              (t) => t.uuid === option.value,
            );
            return (
              <div className="flex flex-col">
                <span>{option.label}</span>
                <span className="text-xs text-secondary">
                  {team?.users?.length || 0} members
                </span>
              </div>
            );
          }}
        />
      </DSModal>
    </div>
  );
};

export default OverviewStep;
